import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useItemisation from '../../Data/Itemisations';
import TopBar from '../../Components/TopBar';
import { PageLoading } from '../../Components/Loading';
import Page from '../../Components/Containers/Page';
import NoItems from '../../Components/EmptyStates/NoItems';
import Section, {Sections} from '../../Components/Containers/Section';
import { ulid } from 'ulid';
import AddCategory from './AddCategory';
import EditCategory from './EditCategory';
import { useDialogues } from '../../Components/Dialogues';
import Money from '../../Components/Formatting/Money';
import Icon from '../../Components/Icons/Icon';
import Button from '../../Components/Button/Button';
import AddItems from './AddItems';
import EditItem from './EditItem';
import Alert from '../../Components/Alert';

const Itemisation = () => {
    const {itemType, itemId, itemisationId} = useParams();
    const {itemisation, update, isSaving} = useItemisation(itemisationId, true);
    const [categories, setCategories] = useState([]);
    const [addingCategory, setAddingCategory] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [addingItems, setAddingItems] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const {confirm} = useDialogues();

    useEffect(() => {
        if (!itemisation) {
            return;
        }
        setCategories([...itemisation.categories]);
    }, [itemisation]);

    const saveChanges = (data) => update(data);

    const addCategory = (data) => {
        setCategories(categories => [
            ...categories,
            Object.assign({
                id: ulid(),
                name: 'Hello',
                description: 'item.pricingCategoryDescription',
                services: [],
                items: []
            }, data)
        ]);
    }

    const replaceCategory = (category, callback) => {
        if (callback && typeof callback === 'function') {
            setCategories(categories => [...categories.map(c => c.id === category ? callback(c) : c)])
        } else {
            setCategories(categories => [...categories.map(c => c.id === category.id ? Object.assign(c, category) : c)])
        }
    }

    const replaceItem = (categoryId, itemId, item) => {
        if (typeof item === 'function') {
            replaceCategory(categoryId, (cat) => Object.assign(cat, {items: [...cat.items.map(i => i.pricingItemId === itemId ? item(i) : i)]}))
        } else {
            replaceCategory(categoryId, (cat) => Object.assign(cat, {items: [...cat.items.map(i => i.pricingItemId === itemId ? item : i)]}))
        }
    }

    const updateItemUnits = (categoryId, itemId, newCount) => {
        replaceItem(categoryId, itemId, (item) => Object.assign(item, {units: newCount}))
    }

    const removeItem = (categoryId, itemId) => {
        replaceCategory(categoryId, (cat) => Object.assign(cat, {items: [...cat.items.filter(i => i.pricingItemId !== itemId)]}))
    }

    const addItemsToCategory = (categoryId, items) => {
        replaceCategory(categoryId, (cat) => Object.assign(cat, {items: [...cat.items, ...items]}))
    }

    const removeCategory = (id) => {
        setCategories(categories => categories.filter(c => c.id !== id));
    }

    const handleSave = (itemisation, data) => {
        // If it's not a draft we can save it right away
        if (!itemisation.draft) {
            return saveChanges(data);
        }

        confirm(
            {
                theme: 'info',
                title: 'Finished editing?',
                message: <>
                    Would you like to share this itemisation with the customer?<br />
                    You can still make changes once it's shared, but you can't mark it as a draft again.
                </>,
                cancelLabel: 'Keep as draft',
                confirmLabel: 'Share with customer',
                onCancel: () => saveChanges({...data, draft: true})
            },
            () => saveChanges({...data, draft: false})
        );
    }

    return <>
        <TopBar left={`/manage/${itemType}/${itemId}/`}
            title={getTitle(itemType, itemisation)}
            right={<Button theme="primary"
                onClick={() => handleSave(itemisation, {categories})}
                label={isSaving ? '' : 'Save'}
                loading={isSaving}
                disabled={isSaving || categories.length === 0}
                zeroMargin
            />}
        />

        <Page>
            {itemisation ?
                <Inner {...{itemisation, categories, setAddingCategory, setEditingCategory, removeCategory, isSaving, setAddingItems, updateItemUnits, setEditingItem}} />
                : <PageLoading />
            }
        </Page>

        <AddCategory isOpen={addingCategory}
            onSave={addCategory}
            onClose={() => setAddingCategory(false)}
            existing={categories}
        />
        <EditCategory category={editingCategory}
            onClose={() => setEditingCategory(null)}
            onSave={replaceCategory}
        />
        <AddItems isOpen={!!addingItems}
            categoryId={addingItems}
            onClose={() => setAddingItems(null)}
            onSave={(items) => addItemsToCategory(addingItems, items)}
            existing={categories.find(c => c.id === addingItems)?.items ?? []}
        />
        <EditItem isOpen={!!editingItem}
            item={editingItem?.item ?? null}
            onClose={() => setEditingItem(null)}
            onSave={(item) => replaceItem(editingItem.categoryId, editingItem.item.pricingItemId, item)}
            removeItem={() => removeItem(editingItem.categoryId, editingItem.item.pricingItemId)}
        />
    </>
}

const getTitle = (itemType, itemisation) => {
    const titles = {
        'quotes': 'Quote',
        'customers': 'Job',
        'Customers/Invoice': 'Invoice',
        'Customers/Job': 'Job',
        'Quotes/Quote': 'Quote',
        'Balance/Invoice': 'Invoice',
    };

    return titles[itemisation?.entityModel ?? itemType] ?? 'Itemisation';
}

const Inner = ({itemisation, categories, setAddingCategory, setEditingCategory, removeCategory, isSaving, setAddingItems, updateItemUnits, setEditingItem}) => {
    return <>
        {itemisation && itemisation.draft && <Alert
            theme="info"
            title="Draft"
            message={<>
                This is still a draft and is not visible to the customer yet.<br />
                You can choose to share it with your customer when you save it.
            </>}
        />}
        <Sections>
            {categories.map(category => <Category key={category.id}
                {...{setEditingCategory, removeCategory, isSaving, setAddingItems, updateItemUnits, setEditingItem, ...category}}
            />)}

            <NoItems short icon="ListCheck" title="Add a category" onClick={() => setAddingCategory(true)} disabled={isSaving} />
        </Sections>
    </>
}

const Category = ({id, name, description, services, items, setEditingCategory, removeCategory, isSaving, setAddingItems, updateItemUnits, setEditingItem}) => {
    const {confirm} = useDialogues();

    return <Section title={name}
        subtitle={description}
        optionsDisabled={isSaving}
        menu={[
            {
                title: 'Edit',
                icon: 'Pencil',
                onClick: () => setEditingCategory({id, name, description, services})
            },
            {
                title: 'Remove',
                icon: 'Trash',
                onClick: () => confirm(
                    {theme: 'danger', confirmLabel: 'Delete', title: 'Remove category', message: `Really remove ${name} and all it's items?`},
                    () => removeCategory(id)
                )
            }
        ]}
    >
        <ul className="-mb-3 divide-y divide-gray-200 text-sm leading-6">
            {items.map((item, i) => <Item key={item.pricingItemId+i} {...{isSaving, updateItemUnits, setEditingItem, categoryId: id, item}} />)}
            <li className="flex pt-1">
                <div className="grow text-right text-muted">
                    Total
                </div>
                <div className="grow-0 shrink-0 px-2 text-right font-bold">
                    <Money pence={items.reduce((total, item) => total + (item.unitPrice * (item.units ?? 1)), 0)} />
                </div>
                <div className="grow-0 shrink-0">
                    {/* Probably a better way to keep the spacing consistent?! */}
                    <button className="p-2 fill-transparent" disabled={true}>
                        <Icon icon="Plus" size="1x" inline />
                    </button>
                </div>
            </li>
        </ul>
        <button disabled={isSaving}
            onClick={() => setAddingItems(id)}
            className="mt-5 py-2 relative block w-full rounded-lg border border-dashed border-gray-300 text-center hover:border-gray-400 focus:outline-none focus:border-brand focus:border-solid text-gray-900 fill-brand disabled:fill-gray-500 disabled:text-gray-500"
        >
            <Icon icon="Plus" className="mt-1" size="1x" inline /> Add items
        </button>
    </Section>
}

const Item = ({categoryId, item, isSaving, updateItemUnits, setEditingItem}) => {
    const {pricingItemId, name, description, displayPrice, unitPrice, units} = item;

    return <li className="flex py-1">
        <div className="grow" onClick={() => setEditingItem({categoryId, item})}>
            {name}
            <button className="ml-2 p-1 fill-muted"
                onClick={() => setEditingItem({categoryId, item})}
                disabled={isSaving}
            >
                <Icon icon="Pencil" size="0.5x" inline />
            </button>
            <br />
            <span className="text-muted">{description}</span>
        </div>
        <div className="grow-0 shrink-0 px-2 text-right" onClick={() => setEditingItem({categoryId, item})}>
            <ItemPrice {...{unitPrice, units, displayPrice}} />
        </div>
        {displayPrice === 'Individual' && <div className="grow-0 shrink-0">
            <button className="p-1 fill-brand disabled:fill-gray-500"
                onClick={() => updateItemUnits(categoryId, pricingItemId, parseInt(units) + 1)}
                disabled={isSaving || displayPrice === 'Total'}
            >
                <Icon icon="Plus" size="1x" inline />
            </button>
        </div>}
    </li>
}

const ItemPrice = ({unitPrice, units, displayPrice}) => {
    if (displayPrice === 'Total') {
        return <Money pence={unitPrice} />
    } else if (displayPrice === 'Individual') {
        return <>
            <Money pence={unitPrice * units} />
            <br />
            <span className="text-muted">{units} x <Money pence={unitPrice} /></span>
        </>
    } else {
        return '???'
    }
}

export default Itemisation;
